import React, { useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import {
    IncomeCalculator,
    DeductionCalculator,
    MonthlyCalculator,
    AnnualCalculator,
    BasicCalculator,
    HealthCalculator,
} from './calculators';
import type { HealthCalculatorInitialData, HealthCalculatorSavePayload } from './calculators/types';

export type CalculatorType = 'income' | 'deduction' | 'monthly' | 'annual' | 'basic' | 'health' | 'none';

interface CalculatorModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: (amount: number) => void;
    currentValue?: string;
    calculatorType?: CalculatorType;
    unit?: 'year' | 'month' | 'none';
    decimalPlaces?: number;
    /** When calculatorType is 'health': initial data for the three tabs. */
    healthInitialData?: HealthCalculatorInitialData;
    /** When calculatorType is 'health': called with amount and payload (toggles + sections). */
    onHealthCalculatorSave?: (amount: number, payload: HealthCalculatorSavePayload) => void;
}

const TITLES: Record<Exclude<CalculatorType, 'none'>, string> = {
    income: 'CALCULATE INCOME',
    deduction: 'CALCULATE DEDUCTION',
    monthly: 'YEARLY TO MONTHLY',
    annual: 'MONTHLY TO ANNUAL',
    basic: 'CALCULATOR',
    health: 'HEALTH INSURANCE CALCULATOR',
};

export const CalculatorModal: React.FC<CalculatorModalProps> = ({
    isOpen,
    onClose,
    onSave,
    currentValue = '',
    calculatorType = 'income',
    unit = 'year',
    decimalPlaces = 2,
    healthInitialData,
    onHealthCalculatorSave,
}) => {
    const theme = useTheme();
    const mouseDownOutsideRef = useRef<boolean>(false);

    const handleMouseDown = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) mouseDownOutsideRef.current = true;
        else mouseDownOutsideRef.current = false;
    };

    const handleMouseUp = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget && mouseDownOutsideRef.current) onClose();
        mouseDownOutsideRef.current = false;
    };

    if (!isOpen) return null;

    const title = calculatorType !== 'none' ? TITLES[calculatorType] : 'CALCULATOR';
    const baseProps = { currentValue, unit, decimalPlaces, onSave, onClose };
    const healthProps = calculatorType === 'health' ? {
        ...baseProps,
        initialData: healthInitialData,
        onSave: onHealthCalculatorSave ?? ((amt: number, _payload: HealthCalculatorSavePayload) => onSave(amt)),
    } : undefined;

    return (
        <div
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
            }}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
        >
            <div
                style={{
                    backgroundColor: theme.colors.surface,
                    borderRadius: theme.borderRadius.lg,
                    padding: theme.spacing.xl,
                    width: '500px',
                    maxWidth: '90vw',
                    boxShadow: theme.shadows.lg,
                }}
                onMouseDown={(e) => e.stopPropagation()}
                onMouseUp={(e) => e.stopPropagation()}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.lg }}>
                    <h2 style={{ fontSize: theme.typography.fontSize.lg, fontWeight: theme.typography.fontWeight.bold, color: theme.colors.text.primary, margin: 0 }}>
                        {title}
                    </h2>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: theme.typography.fontSize.xl,
                            color: theme.colors.text.secondary,
                            cursor: 'pointer',
                            padding: theme.spacing.xs,
                            borderRadius: theme.borderRadius.sm,
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = theme.colors.surfaceSecondary; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                        ×
                    </button>
                </div>
                {calculatorType === 'income' && <IncomeCalculator {...baseProps} />}
                {calculatorType === 'deduction' && <DeductionCalculator {...baseProps} />}
                {calculatorType === 'monthly' && <MonthlyCalculator {...baseProps} />}
                {calculatorType === 'annual' && <AnnualCalculator {...baseProps} />}
                {calculatorType === 'basic' && <BasicCalculator {...baseProps} />}
                {calculatorType === 'health' && healthProps && <HealthCalculator {...healthProps} />}
            </div>
        </div>
    );
};
